import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { Empty } from "antd";
import dayjs from "dayjs";
import { Bell, Pencil, Search } from "lucide-react";
import {
  Get_Associate_Members,
  Get_KiiValuesFormDateToDate,
  Get_User_Last_Active_Status,
  Get_User_Performance_Data_For_Months,
  Get_User_Profile,
  Get_User_Value_For_Project_Time,
  GET_ATTENDANCE_DETAILS,
  GET_User_For_Account,
} from "../../services/userAPI";
import UserProfileUserIcon from "../../Images/userProfileUserIcon.png";

const filterOptions = [
  { key: "thisMonth", label: "This month" },
  { key: "lastMonth", label: "Last month" },
  { key: "last3", label: "Last 3 months" },
  { key: "last6", label: "Last 6 months" },
  { key: "lastYear", label: "Last Year" },
  { key: "custom", label: "Custom" },
];

const getRange = (filter) => {
  const now = dayjs();
  switch (filter) {
    case "lastMonth":
      return { start: now.subtract(1, "month").startOf("month"), end: now.subtract(1, "month").endOf("month") };
    case "last3":
      return { start: now.subtract(3, "month").startOf("month"), end: now.endOf("day") };
    case "last6":
      return { start: now.subtract(6, "month").startOf("month"), end: now.endOf("day") };
    case "lastYear":
      return { start: now.subtract(1, "year").startOf("month"), end: now.endOf("day") };
    default:
      return { start: now.startOf("month"), end: now.endOf("day") };
  }
};

const InfoItem = ({ label, value }) => (
  <div>
    <p className="text-[12px] text-[#888888]">{label}</p>
    <p className="mt-0.5 text-[13px] font-medium text-[#1F2020]">{value || "-"}</p>
  </div>
);

const RoleUserProfile = ({ roleId, accountId, selectedUserId, onBack, onSelectUser }) => {
  const [userSearch, setUserSearch] = useState("");
  const [filter, setFilter] = useState("thisMonth");
  const [customRange, setCustomRange] = useState({ start: "", end: "" });

  const { data: usersData } = useQuery(GET_User_For_Account, {
    variables: { roleId: parseInt(roleId), pagingCriteria: { limit: 500 } },
    skip: !roleId,
    fetchPolicy: "network-only",
  });
  const users = usersData?.getUsersForAccount?.data || [];

  const range = useMemo(() => {
    if (filter === "custom" && customRange.start && customRange.end) {
      return { start: dayjs(customRange.start), end: dayjs(customRange.end) };
    }
    return getRange(filter);
  }, [filter, customRange]);

  const startDate = range.start.format("YYYY-MM-DD");
  const endDate = range.end.format("YYYY-MM-DD");

  const { data: profileData } = useQuery(Get_User_Profile, {
    variables: { userId: parseInt(selectedUserId) },
    skip: !selectedUserId,
    fetchPolicy: "network-only",
  });
  const user = profileData?.userProfile;
  const isEndUser = user?.userType?.userType === "End User";

  const { data: lastActiveData } = useQuery(Get_User_Last_Active_Status, {
    variables: { userId: parseInt(selectedUserId) },
    skip: !selectedUserId,
    fetchPolicy: "network-only",
  });
  const lastActive = lastActiveData?.getUserLastActiveStatus;

  const { data: attendanceData, loading: attendanceLoading } = useQuery(GET_ATTENDANCE_DETAILS, {
    variables: { userId: parseInt(selectedUserId), startDate, endDate },
    skip: !selectedUserId,
    fetchPolicy: "network-only",
  });
  const attendanceRows = (attendanceData?.getAttendanceDetailsForATimePeriod || [])
    .slice()
    .sort((a, b) => dayjs(a.inputDate).unix() - dayjs(b.inputDate).unix());

  const presentDays = attendanceRows.filter((r) => r.didSignIn).length;
  const complianceDays = attendanceRows.filter((r) => r.didInput).length;
  const leaveDays = attendanceRows.filter((r) => r.isLeave).length;
  const holidayDays = attendanceRows.filter((r) => r.isHoliday || r.isWeekoff).length;

  const { data: kiiData, loading: kiiLoading } = useQuery(Get_KiiValuesFormDateToDate, {
    variables: {
      accountId: parseInt(accountId),
      jobroleId: parseInt(roleId),
      userId: parseInt(selectedUserId),
      startDate,
      endDate,
    },
    skip: !isEndUser || !selectedUserId || !roleId || !accountId,
    fetchPolicy: "network-only",
  });
  const kiiRows = kiiData?.getKiiValuesFormDateToDate || [];

  const { data: projectTimeData, loading: projectTimeLoading } = useQuery(Get_User_Value_For_Project_Time, {
    variables: {
      userId: parseInt(selectedUserId),
      startDate: `${startDate}T00:00:00`,
      endDate: `${endDate}T23:59:59`,
    },
    skip: !isEndUser || !selectedUserId,
    fetchPolicy: "network-only",
  });
  const projectTimeRows = projectTimeData?.getUserValueForProjectTimeForATimePeriod || [];

  const { data: performanceData, loading: performanceLoading } = useQuery(Get_User_Performance_Data_For_Months, {
    variables: {
      userId: parseInt(selectedUserId),
      fromMonth: `${startDate}T00:00:00`,
      toMonth: `${endDate}T23:59:59`,
    },
    skip: !isEndUser || !selectedUserId,
    fetchPolicy: "network-only",
  });
  const performanceRows = performanceData?.getUserPerformanceDataForMonths || [];
  const maxPerformance = Math.max(...performanceRows.map((r) => parseFloat(r.performance) || 0), 100);

  // Manager tree
  const treeManagerId = user?.reporting?.id || user?.id || selectedUserId;
  const { data: teamData, loading: teamLoading } = useQuery(Get_Associate_Members, {
    variables: { userId: parseInt(treeManagerId) },
    skip: isEndUser || !treeManagerId,
    fetchPolicy: "network-only",
  });
  const teamMembers = teamData?.getAssocateMembers || [];
  const treeManagerNode = user?.reporting?.id ? user.reporting : user;

  const filteredUsers = users.filter((u) =>
    (u.fullname || u.name || "").toLowerCase().includes(userSearch.toLowerCase())
  );

  const attendanceStatus = (r) => {
    if (r.isHoliday || r.isWeekoff) return "Holiday";
    if (r.isLeave) return "Leave";
    if (r.didSignIn) return "Present";
    return "Absent";
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <h2 className="text-[18px] font-semibold text-[#1F2020]">Cohort Management</h2>

      {/* Search */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search User"
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          className="h-[44px] w-full rounded-[8px] border border-[#D9DDE3] bg-white px-4 pr-10 text-[13px] outline-none focus:border-[#AD0101]"
        />
        <Search className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#888888]" />
      </div>

      <div className="flex gap-4">
        {/* Users list */}
        <div className="w-[220px] shrink-0 rounded-[12px] bg-white p-4 shadow-[0_4px_15px_rgba(0,0,0,0.04)]">
          <p className="mb-3 text-[13px] font-semibold text-[#1F2020]">Users</p>
          <div className="flex flex-col gap-2">
            {filteredUsers.map((u) => {
              const isActive = parseInt(u.id) === parseInt(selectedUserId);
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => onSelectUser?.(u)}
                  className={`flex items-center gap-2 rounded-[8px] border px-3 py-2 text-left transition-colors ${
                    isActive ? "border-[#AD0101] bg-[#FFF2F2]" : "border-[#E8E8E8] hover:bg-[#F7F8FA]"
                  }`}
                >
                  <img
                    src={u.profilePicture || UserProfileUserIcon}
                    alt={u.name}
                    className="h-7 w-7 rounded-full object-cover"
                  />
                  <span className="truncate text-[13px] font-medium text-[#1F2020]">
                    {u.fullname || u.name}
                  </span>
                </button>
              );
            })}
            {!filteredUsers.length && <p className="text-[12px] text-[#888888]">No users found.</p>}
          </div>
        </div>

        {/* Profile content */}
        <div className="flex flex-1 flex-col gap-4">
          {/* Profile card */}
          <div className="flex items-center justify-between rounded-[12px] bg-white px-5 py-4 shadow-[0_4px_15px_rgba(0,0,0,0.04)]">
            <div className="flex items-center gap-3">
              <img
                src={user?.profilePicture || UserProfileUserIcon}
                alt={user?.name}
                className="h-[52px] w-[52px] rounded-full object-cover"
              />
              <div>
                <p className="text-[15px] font-semibold text-[#1F2020]">
                  {user?.fullname || [user?.name, user?.surname].filter(Boolean).join(" ") || "-"}
                </p>
                <p className="text-[12px] text-[#888888]">{user?.email || "-"}</p>
                <p className="text-[11px] text-[#888888]">
                  <span
                    className={`mr-1 inline-block h-2 w-2 rounded-full ${
                      typeof lastActive === "string" && lastActive.startsWith("Online")
                        ? "bg-[#2BAF4A]"
                        : "bg-[#B7B6B6]"
                    }`}
                  />
                  {lastActive || "Offline"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-[#888888]">
              <Pencil className="h-4 w-4 cursor-pointer hover:text-[#AD0101]" />
              <Bell className="h-4 w-4 cursor-pointer hover:text-[#AD0101]" />
            </div>
          </div>

          {/* Basic + Work info */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-[12px] bg-white p-5 shadow-[0_4px_15px_rgba(0,0,0,0.04)]">
              <h3 className="mb-4 text-[14px] font-semibold text-[#1F2020]">Basic information</h3>
              <div className="grid grid-cols-2 gap-4">
                <InfoItem label="Name" value={user?.name} />
                <InfoItem label="Gender" value={user?.gender} />
                <InfoItem label="Date of Birth" value={user?.dateOfBirth ? dayjs(user.dateOfBirth).format("DD-MM-YYYY") : "-"} />
                <InfoItem label="Employee ID" value={user?.employeeId} />
                <InfoItem label="Email ID" value={user?.email} />
                <InfoItem label="Highest Qualification" value={user?.higestEducationQualification} />
                <InfoItem label="Previous Employer" value={user?.previousEmployer} />
                <InfoItem label="Previous Experience" value={user?.previousExperience ? `${user.previousExperience}` : "No"} />
                <InfoItem label="Phone Number" value={user?.phoneNumber} />
                <InfoItem
                  label="Preferred Video Language"
                  value={(user?.preferredLanguages || []).map((l) => l.name).filter(Boolean).join(", ")}
                />
                <InfoItem label="App Language" value={user?.appLanguage?.language} />
              </div>
            </div>

            <div className="rounded-[12px] bg-white p-5 shadow-[0_4px_15px_rgba(0,0,0,0.04)]">
              <h3 className="mb-4 text-[14px] font-semibold text-[#1F2020]">Work information</h3>
              <div className="grid grid-cols-2 gap-4">
                <InfoItem label="Employment" value={user?.employeeType} />
                <InfoItem
                  label="Date of Joining"
                  value={
                    user?.joiningDate
                      ? `${dayjs(user.joiningDate).format("D/M/YYYY")} (${Math.floor(
                          dayjs().diff(dayjs(user.joiningDate), "day") / 30
                        )} month ${dayjs().diff(dayjs(user.joiningDate), "day") % 30} days)`
                      : "-"
                  }
                />
                <InfoItem label="Current Designation" value={user?.designation} />
                <InfoItem label="Branch" value={user?.branch} />
                <InfoItem
                  label="State, City"
                  value={[user?.stateName, user?.cityName].filter(Boolean).join(", ") || "-"}
                />
                <InfoItem label="Login Type" value={user?.userType?.userType} />
                <InfoItem label="Manager Name" value={user?.reporting?.name || "No Manager"} />
                <InfoItem label="Manager ID" value={user?.reporting?.employeeId} />
                <InfoItem
                  label="Date of Reporting"
                  value={user?.reportingDate ? dayjs(user.reportingDate).format("D/M/YYYY") : "-"}
                />
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 rounded-[12px] bg-white px-4 py-3 shadow-[0_4px_15px_rgba(0,0,0,0.04)]">
            <span className="mr-1 text-[13px] font-semibold text-[#1F2020]">Filters</span>
            {filterOptions.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={`rounded-[6px] px-3 py-1.5 text-[12px] font-medium transition-colors ${
                  filter === f.key ? "bg-[#AD0101] text-white" : "bg-[#F7F8FA] text-[#1F2020] hover:bg-[#EFEFEF]"
                }`}
              >
                {f.label}
              </button>
            ))}
            {filter === "custom" && (
              <span className="flex items-center gap-2">
                <input
                  type="date"
                  value={customRange.start}
                  onChange={(e) => setCustomRange((p) => ({ ...p, start: e.target.value }))}
                  className="h-[32px] rounded-[6px] border border-[#D9DDE3] px-2 text-[12px] outline-none"
                />
                <input
                  type="date"
                  value={customRange.end}
                  onChange={(e) => setCustomRange((p) => ({ ...p, end: e.target.value }))}
                  className="h-[32px] rounded-[6px] border border-[#D9DDE3] px-2 text-[12px] outline-none"
                />
              </span>
            )}
          </div>

          {/* Attendance Summary */}
          <div className="rounded-[12px] bg-white p-5 shadow-[0_4px_15px_rgba(0,0,0,0.04)]">
            <h3 className="mb-4 text-[14px] font-semibold text-[#1F2020]">
              Attendance Summary ({range.start.format("DD/MM/YYYY")} - {range.end.format("DD/MM/YYYY")})
            </h3>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {[
                { label: "Present Days", value: presentDays, color: "#2BAF4A" },
                { label: "Compliance Day", value: complianceDays, color: "#4B7BD6" },
                { label: "Days On Leave", value: leaveDays, color: "#F5A623" },
                { label: "Holiday", value: holidayDays, color: "#AD0101" },
              ].map((c) => (
                <div key={c.label} className="rounded-[10px] border border-[#F0F0F0] bg-[#FAFAFA] py-4 text-center">
                  <p className="text-[12px] text-[#888888]">{c.label}</p>
                  <p className="mt-1 text-[18px] font-bold" style={{ color: c.color }}>
                    {c.value}
                  </p>
                </div>
              ))}
            </div>

            {attendanceLoading ? (
              <p className="mt-4 text-center text-[12px] text-[#888888]">Loading attendance...</p>
            ) : attendanceRows.length ? (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full min-w-[560px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-[#E8E8E8]">
                      {["Date", "Sign In", "Sign Out", "Time Distribution", "Status"].map((h) => (
                        <th key={h} className="px-3 py-2 text-[12px] font-semibold text-[#1F2020]">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceRows.map((r, i) => (
                      <tr key={i} className="border-b border-[#F0F0F0] last:border-0">
                        <td className="px-3 py-2 text-[12px]">{dayjs(r.inputDate).format("DD/MM/YYYY")}</td>
                        <td className="px-3 py-2 text-[12px]">{r.didSignIn ? "Yes" : "No"}</td>
                        <td className="px-3 py-2 text-[12px]">{r.didSignOut ? "Yes" : "No"}</td>
                        <td className="px-3 py-2 text-[12px]">{r.givenTimeDistribution ? "Yes" : "No"}</td>
                        <td className="px-3 py-2 text-[12px] font-medium">{attendanceStatus(r)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Empty description="No attendance records" className="mt-4" />
            )}
          </div>

          {/* End user sections */}
          {isEndUser && (
            <>
              {/* Lead Indicators */}
              <div className="rounded-[12px] bg-white p-5 shadow-[0_4px_15px_rgba(0,0,0,0.04)]">
                <h3 className="mb-4 text-[14px] font-semibold text-[#1F2020]">Lead Indicators ({kiiRows.length})</h3>
                {kiiLoading ? (
                  <p className="text-center text-[12px] text-[#888888]">Loading lead indicators...</p>
                ) : kiiRows.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[560px] border-collapse text-left">
                      <thead>
                        <tr className="border-b border-[#E8E8E8]">
                          {["Lead Indicator", "Min", "Max", "Avg", "Total", "Target"].map((h) => (
                            <th key={h} className="px-3 py-2 text-[12px] font-semibold text-[#1F2020]">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {kiiRows.map((r, i) => (
                          <tr key={i} className="border-b border-[#F0F0F0] last:border-0">
                            <td className="px-3 py-2 text-[12px] font-medium">{r.kiiName}</td>
                            <td className="px-3 py-2 text-[12px]">{r.value?.min ?? "-"}</td>
                            <td className="px-3 py-2 text-[12px]">{r.value?.max ?? "-"}</td>
                            <td className="px-3 py-2 text-[12px]">{r.value?.avg ?? "-"}</td>
                            <td className="px-3 py-2 text-[12px]">{r.value?.totalValue ?? "-"}</td>
                            <td className="px-3 py-2 text-[12px]">{r.value?.totalTarget ?? "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Empty description="No lead indicators" />
                )}
              </div>

              {/* Time Distribution */}
              <div className="rounded-[12px] bg-white p-5 shadow-[0_4px_15px_rgba(0,0,0,0.04)]">
                <h3 className="mb-4 text-[14px] font-semibold text-[#1F2020]">Time Distribution</h3>
                {projectTimeLoading ? (
                  <p className="text-center text-[12px] text-[#888888]">Loading time distribution...</p>
                ) : projectTimeRows.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[560px] border-collapse text-left">
                      <thead>
                        <tr className="border-b border-[#E8E8E8]">
                          {["Project", "Norm", "Depends", "Value", "Status"].map((h) => (
                            <th key={h} className="px-3 py-2 text-[12px] font-semibold text-[#1F2020]">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {projectTimeRows.map((r, i) => (
                          <tr key={i} className="border-b border-[#F0F0F0] last:border-0">
                            <td className="px-3 py-2 text-[12px] font-medium">{r.projectName}</td>
                            <td className="px-3 py-2 text-[12px]">{r.projectNorm ?? "-"}</td>
                            <td className="px-3 py-2 text-[12px]">{r.depends ?? "-"}</td>
                            <td className="px-3 py-2 text-[12px]">{r.userValue ?? "-"}</td>
                            <td className="px-3 py-2 text-[12px]">{r.status ?? "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Empty description="No time distribution data" />
                )}
              </div>

              {/* Performance Data */}
              <div className="rounded-[12px] bg-white p-5 shadow-[0_4px_15px_rgba(0,0,0,0.04)]">
                <h3 className="mb-4 text-[14px] font-semibold text-[#1F2020]">Performance Data</h3>
                {performanceLoading ? (
                  <p className="text-center text-[12px] text-[#888888]">Loading performance...</p>
                ) : performanceRows.length ? (
                  <div className="flex h-[220px] items-end gap-6 px-4">
                    {performanceRows.map((r, i) => {
                      const pct = ((parseFloat(r.performance) || 0) / maxPerformance) * 100;
                      return (
                        <div key={i} className="flex flex-1 flex-col items-center justify-end">
                          <span className="mb-1 text-[11px] font-medium text-[#1F2020]">{r.performance}</span>
                          <div
                            className="w-full max-w-[60px] rounded-t-[6px] bg-[#AD0101]"
                            style={{ height: `${Math.max(pct, 2)}%` }}
                          />
                          <span className="mt-2 text-[11px] text-[#888888]">
                            {r.performanceMonth ? dayjs(r.performanceMonth).format("MMM") : ""}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <Empty description="No performance data" />
                )}
              </div>
            </>
          )}

          {/* Manager tree */}
          {!isEndUser && user && (
            <div className="rounded-[12px] bg-white p-5 shadow-[0_4px_15px_rgba(0,0,0,0.04)]">
              <h3 className="mb-8 text-center text-[15px] font-semibold text-[#1F2020]">
                Manager (1) / Users ({teamMembers.length})
              </h3>
              {teamLoading ? (
                <p className="text-center text-[12px] text-[#888888]">Loading team...</p>
              ) : (
                <div className="flex flex-col items-center">
                  <div className="flex flex-col items-center">
                    <img
                      src={treeManagerNode?.profilePicture || UserProfileUserIcon}
                      alt={treeManagerNode?.name}
                      className="h-[64px] w-[64px] rounded-full border-2 border-white object-cover shadow-md"
                    />
                    <span className="mt-1 rounded-[3px] border border-[#AD0101] bg-white px-2 py-[1px] text-[12px] font-medium text-[#1F2020]">
                      {treeManagerNode?.name}
                    </span>
                    <span className="mt-0.5 rounded-[2px] bg-[#FFD84D] px-1.5 py-[1px] text-[9px] font-semibold text-[#1F2020]">
                      {user?.reporting?.id ? "Manager" : user?.userType?.userType || "Manager"}
                    </span>
                  </div>
                  {teamMembers.length > 0 ? (
                    <>
                      <div className="h-10 w-px bg-[#C4C4C4]" />
                      <div className="flex justify-center">
                        {teamMembers.map((member, index) => (
                          <div key={member.id} className="relative flex w-[110px] flex-col items-center pt-8">
                            {index > 0 && <span className="absolute left-0 top-0 h-px w-1/2 bg-[#C4C4C4]" />}
                            {index < teamMembers.length - 1 && (
                              <span className="absolute right-0 top-0 h-px w-1/2 bg-[#C4C4C4]" />
                            )}
                            <span className="absolute left-1/2 top-0 h-7 w-px -translate-x-1/2 bg-[#C4C4C4]" />
                            <span className="absolute left-1/2 top-7 h-2 w-2 -translate-x-1/2 rounded-full bg-[#C4C4C4]" />
                            <img
                              src={member.profilePicture || UserProfileUserIcon}
                              alt={member.name}
                              className="h-[56px] w-[56px] rounded-full border-2 border-white object-cover shadow"
                            />
                            <span className="mt-1 max-w-[100px] truncate rounded-[3px] border border-[#AD0101] bg-white px-2 py-[1px] text-[11px] font-medium text-[#1F2020]">
                              {member.name}
                            </span>
                            {member.userType?.userType && (
                              <span className="mt-0.5 rounded-[2px] bg-[#FFD84D] px-1.5 py-[1px] text-[9px] font-semibold text-[#1F2020]">
                                {member.userType.userType}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => onSelectUser?.(member)}
                              className="mt-1.5 rounded-[3px] border border-[#AD0101] bg-white px-3 py-[2px] text-[10px] font-medium text-[#AD0101] transition-colors hover:bg-[#FFF2F2]"
                            >
                              View Profile
                            </button>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="mt-4 text-[12px] text-[#888888]">No team members found.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleUserProfile;
